
// Utility to detect if a string matches SHA-256 hash pattern (64 hex characters)
export function isSha256(str: string): boolean {
    return /^[a-f0-9]{64}$/i.test(str);
}

// Client-side SHA-256 (matches server implementation)
export async function sha256(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface BlockDisplay {
    id: string; // Grouping Key (usually email or Participant ID, or raw hash)
    email: string | null; // Resolved email or null
    identifier_value: string; // Original identifier value
    identifier_types: Set<string>; // 'participant_id', 'email_hash', etc.
    scope: 'org' | 'contest'; // Broadest scope wins
    contest_ids: Set<string>; // Contests involved
    contest_titles: Set<string>; // Contest titles
    reasons: Set<string>;
    expires_at: string | null | 'MIN_DATE'; // Earliest expiry? Or latest? Let's say: if any is null (forever), then null. Else max.
    created_at: string; // Min created_at
    block_ids: string[]; // All abuse_block.ids in this group
}

/**
 * Groups abuse blocks into person-centric rows.
 * Attempts to resolve emails from participant data.
 */
export async function processAbuseBlocks(blocks: any[], participants: any[], contestsMap: Map<string, string>): Promise<BlockDisplay[]> {
    const rows = new Map<string, BlockDisplay>();

    // Helper to get or create row
    const getRow = (key: string, email: string | null = null) => {
        if (!rows.has(key)) {
            rows.set(key, {
                id: key,
                email: email,
                identifier_value: key, // Fallback display unless email is resolved
                identifier_types: new Set(),
                scope: 'contest', // Default narrowest, upgrade to org if found
                contest_ids: new Set(),
                contest_titles: new Set(),
                reasons: new Set(),
                expires_at: 'MIN_DATE',
                created_at: new Date().toISOString(),
                block_ids: []
            });
        }
        return rows.get(key)!;
    };

    // Index participants for fast lookup
    const pById = new Map<string, any>();
    const emailToHash = new Map<string, string>();
    const hashToEmail = new Map<string, string>();

    // Hash unique emails
    const uniqueEmails = Array.from(new Set(participants.map((p: any) => p.email).filter(Boolean)));
    for (const email of uniqueEmails) {
        const normalized = email.toLowerCase().trim();
        const hash = await sha256(normalized);
        emailToHash.set(normalized, hash);
        hashToEmail.set(hash, normalized);
    }

    participants.forEach((p: any) => {
        pById.set(p.id, p);
    });

    // Group IP and Device
    const pByIp = new Map<string, any[]>();
    const pByDevice = new Map<string, any[]>();
    participants.forEach((p: any) => {
        if (p.ip_hash) {
            if (!pByIp.has(p.ip_hash)) pByIp.set(p.ip_hash, []);
            pByIp.get(p.ip_hash)!.push(p);
        }
        if (p.device_hash) {
            if (!pByDevice.has(p.device_hash)) pByDevice.set(p.device_hash, []);
            pByDevice.get(p.device_hash)!.push(p);
        }
    });

    // Process blocks
    for (const block of blocks) {
        let groupingKey: string = block.value;
        let resolvedEmail: string | null = null;

        // Try to resolve to an Email (Primary Grouping Key)
        if (block.identifier_type === 'email_hash') {
            if (hashToEmail.has(block.value)) {
                resolvedEmail = hashToEmail.get(block.value)!;
                groupingKey = resolvedEmail;
            }
        } else if (block.identifier_type === 'participant_id') {
            const p = pById.get(block.value);
            if (p?.email) {
                resolvedEmail = p.email;
                groupingKey = resolvedEmail;
            }
        } else if (block.identifier_type === 'ip_hash') {
            const ps = pByIp.get(block.value);
            // Heuristic: pick most recent participant with this IP
            if (ps && ps.length > 0) {
                const p = ps[0]; // just pick one to identify the "person"
                if (p.email) {
                    resolvedEmail = p.email;
                    groupingKey = resolvedEmail;
                }
            }
        } else if (block.identifier_type === 'device_hash') {
            const ps = pByDevice.get(block.value);
            if (ps && ps.length > 0) {
                const p = ps[0];
                if (p.email) {
                    resolvedEmail = p.email;
                    groupingKey = resolvedEmail;
                }
            }
        }

        const row = getRow(groupingKey, resolvedEmail);

        // Update Row Data
        row.block_ids.push(block.id);
        row.identifier_types.add(block.identifier_type);
        row.reasons.add(block.reason);

        if (block.scope === 'org') row.scope = 'org';
        if (block.contest_id) {
            row.contest_ids.add(block.contest_id);
            const title = contestsMap.get(block.contest_id);
            if (title) row.contest_titles.add(title);
        }

        // Expiry Logic: Max effective block (Latest date) or Null (Forever)
        // If any block is permanent (null), the row is effectively permanent -> null.
        if (row.expires_at !== null) { // If not already deemed permanent
            if (block.expires_at === null) {
                row.expires_at = null; // Found a permanent block
            } else {
                // It's a date.
                if (row.expires_at === 'MIN_DATE') {
                    row.expires_at = block.expires_at;
                } else {
                    const current = new Date(row.expires_at).getTime();
                    const newDate = new Date(block.expires_at).getTime();
                    if (newDate > current) {
                        row.expires_at = block.expires_at;
                    }
                }
            }
        }

        // Created At: Min (Earliest block)
        if (!row.created_at || new Date(block.created_at) < new Date(row.created_at)) {
            row.created_at = block.created_at;
        }
    }

    // Convert Set to Array for display? No, keep pure object, handling in UI.
    const finalRows = Array.from(rows.values());

    // Clean up 'MIN_DATE' placeholder
    finalRows.forEach(row => {
        if (row.expires_at === 'MIN_DATE') {
            // This implies only expired blocks were processed? Or logic error?
            // If no blocks had expiry info (impossible if db constrained), or no blocks processed.
            // If blocks exist, they have either null or date.
            // So if it remains MIN_DATE, it means NO blocks were processed for this row (impossible).
            row.expires_at = null;
        }
    });

    return finalRows.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}
